import type { Exercise } from '../../types.ts';
export const batch3Foundations: Exercise[] = [
  {
    id: 'foundations-atoms',
    category: 'foundations',
    title: 'Atoms, isotopes and ions',
    topic: 'Atomic identity, particle counts and isotope averages',
    difficulty: 'Challenging',
    reading: {
      title: 'Three different ways an atom can change',
      sources: [
        {
          title: 'OpenStax Chemistry 2e · Atomic Structure and Symbolism (CC BY 4.0)',
          url: 'https://openstax.org/books/chemistry-2e/pages/2-3-atomic-structure-and-symbolism',
        },
      ],
      sections: [
        {
          id: 'particles',
          title: 'Start with the particles',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An atom contains a compact nucleus surrounded by an electron distribution. The nucleus contains protons, which have positive electric charge, and usually neutrons, which have no net charge. Electrons have negative charge. A proton and an electron have equal charge magnitudes and opposite signs, but their masses differ greatly: an electron is much lighter. Most of an atom’s mass is therefore concentrated in its nucleus, while its electron distribution accounts for its much larger overall size.',
            },
            {
              kind: 'paragraph',
              text: 'A picture of electrons on neat circular tracks is a useful early mnemonic but is not a literal account of their motion. For this unit, particle counts are enough; we do not need to assign a tiny planetary orbit to each electron. The practical first step is to keep three separate ledgers: how many protons, how many neutrons and how many electrons. Changing one ledger has a different meaning from changing another.',
            },
            {
              kind: 'table',
              caption: 'Particle bookkeeping',
              headers: ['Particle', 'Charge in elementary units', 'Main location'],
              rows: [
                ['Proton', '+1', 'Nucleus'],
                ['Neutron', '0', 'Nucleus'],
                ['Electron', '−1', 'Electron distribution'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'The atomic number Z is the number of protons. It defines the element: all atoms with eight protons are oxygen, whatever their neutron or electron counts. Element identity therefore cannot be decided from total mass alone. Two species can have the same mass number yet be different elements, and two species can have different masses yet belong to the same element.',
            },
          ],
        },
        {
          id: 'isotopes',
          title: 'Separate identity from nuclear mass number',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The mass number A counts protons plus neutrons in a particular nucleus. It is an integer count, not the precise measured mass of that atom in atomic mass units. Because the proton count is Z, the neutron count is A − Z. Subtracting works because the total counted particles have been split into two nonoverlapping categories.',
            },
            {
              kind: 'equation',
              label: 'Nuclear counts',
              lines: [
                {
                  left: 'neutrons',
                  right: 'A − Z',
                  reason:
                    'Mass number counts protons and neutrons; atomic number counts only protons.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Isotopes are atoms of the same element with different neutron counts. They share Z but differ in A. For example, carbon-12 and carbon-13 both have six protons, but their neutron counts are six and seven. The hyphenated number names the mass number, not the number of electrons. Isotopes can differ in nuclear stability and physical properties even though their electron structures and chemical behavior are often very similar.',
            },
            {
              kind: 'example',
              title: 'A different nucleus',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'An atom has atomic number 10 and mass number 22. It has 10 protons and 22 − 10 = 12 neutrons. A species with atomic number 11 and mass number 22 has the same mass number but is a different element, because one proton has replaced one neutron in the count. Equal mass number does not make these two species isotopes of one element.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'When reading a symbol or prompt, identify which number is given before subtracting. A common reversal is to treat the mass number as the neutron count and then add protons again, double-counting them. Another is to read a periodic-table average as the mass number of every individual atom; an average over a mixture need not equal any one member’s mass.',
            },
          ],
        },
        {
          id: 'ions',
          title: 'Charge counts electrons against protons',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A neutral atom has equal numbers of protons and electrons, so their charges cancel. An ion has a net electric charge because the counts differ. In ordinary ion formation by electron transfer, the nucleus does not change. Losing a negatively charged electron makes the ion more positive; gaining one makes it more negative. This is why an anion has extra electrons rather than extra protons.',
            },
            {
              kind: 'equation',
              label: 'Charge ledger',
              lines: [
                {
                  left: 'charge in elementary units',
                  right: 'protons − electrons',
                  reason: 'A charge of +2 means two fewer electrons than protons.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Build a positive ion',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A neutral magnesium atom has 12 protons and 12 electrons. Losing two electrons leaves 10 electrons and charge 12 − 10 = +2. Its element remains magnesium because the proton count is unchanged. If its mass number is 24, it still has 12 neutrons after this electron loss.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Charge and isotope are independent labels. A species can be an isotope of an element and also be charged. To reconstruct all three particle counts, use atomic number for protons, mass number minus atomic number for neutrons, and proton count minus signed charge for electrons. The word signed matters: subtracting a charge of −1 adds one electron. Ordinary chemical ion formation should not be confused with a nuclear process that changes the proton count and therefore the element.',
            },
          ],
        },
        {
          id: 'averages',
          title: 'Average atomic mass describes a population',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A natural sample may contain several isotopes. Its average atomic mass weights each isotope’s measured mass by its fraction of the atoms. These fractions refer to counts of atoms, not percentages of the sample’s total mass. If one isotope is common and another rare, their masses should not receive equal weight.',
            },
            {
              kind: 'equation',
              label: 'A two-isotope average',
              lines: [
                {
                  left: 'average mass',
                  right: 'f × m₁ + (1 − f) × m₂',
                  reason:
                    'Here f is the fraction of atoms of isotope 1, and the two fractions sum to one.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A simplified isotope mixture',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Imagine atoms with masses exactly 20 u and 22 u, occurring in fractions 0.75 and 0.25. The average is 0.75 × 20 + 0.25 × 22 = 20.5 u. In a representative group of four atoms, three contribute 20 u and one contributes 22 u: total 82 u divided by four gives the same result.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'No atom in this simplified mixture has mass 20.5 u. The average describes the mixture, just as an average household size need not be a possible household size. Increasing the fraction of the heavier isotope moves the average toward its mass without changing either isotope’s individual mass. Real isotopic masses are not generally exact integers because nuclear binding and particle masses matter; use values supplied in a calculation rather than silently replacing measured masses with mass numbers.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b3-atom-1',
        prompt:
          'An isolated species has 9 protons, 10 neutrons and 10 electrons. What is its net charge in elementary charge units?',
        options: [
          {
            id: '0',
            text: '+1',
          },
          {
            id: '1',
            text: '0',
          },
          {
            id: '2',
            text: '−1',
          },
          {
            id: '3',
            text: '−2',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Nine positive proton charges and ten negative electron charges leave net charge −1.',
        sectionIds: ['particles', 'ions'],
        evidence:
          'Nine positive proton charges and ten negative electron charges leave net charge −1.',
      },
      {
        id: 'b3-atom-2',
        prompt:
          'Two neutral atoms have the same proton count but different neutron counts. How are they related?',
        options: [
          {
            id: '0',
            text: 'They are isotopes of the same element.',
          },
          {
            id: '1',
            text: 'They must be ions of opposite charge.',
          },
          {
            id: '2',
            text: 'They are different elements with equal mass number.',
          },
          {
            id: '3',
            text: 'They have different atomic numbers.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Element identity is fixed by protons; different neutron counts at the same proton count define isotopes.',
        sectionIds: ['particles', 'isotopes'],
        evidence:
          'Element identity is fixed by protons; different neutron counts at the same proton count define isotopes.',
      },
      {
        id: 'b3-atom-3',
        prompt:
          'A species has atomic number 13, mass number 27 and charge +3. How many electrons does it have?',
        options: [
          {
            id: '0',
            text: '13',
          },
          {
            id: '1',
            text: '16',
          },
          {
            id: '2',
            text: '14',
          },
          {
            id: '3',
            text: '10',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'There are 13 protons; a +3 charge means three fewer electrons, leaving 10.',
        sectionIds: ['particles', 'ions'],
        evidence: 'There are 13 protons; a +3 charge means three fewer electrons, leaving 10.',
      },
      {
        id: 'b3-atom-4',
        prompt: 'An atom with atomic number 17 and mass number 37 contains how many neutrons?',
        options: [
          {
            id: '0',
            text: '17',
          },
          {
            id: '1',
            text: '20',
          },
          {
            id: '2',
            text: '37',
          },
          {
            id: '3',
            text: '54',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Mass number counts protons plus neutrons, so the neutron count is 37 − 17 = 20.',
        sectionIds: ['particles', 'isotopes'],
        evidence: 'Mass number counts protons plus neutrons, so the neutron count is 37 − 17 = 20.',
      },
      {
        id: 'b3-atom-5',
        prompt:
          'A neutral atom gains two electrons without any nuclear change. Which statement must be true?',
        options: [
          {
            id: '0',
            text: 'It becomes a 2− ion of the same element.',
          },
          {
            id: '1',
            text: 'Its atomic number rises by two.',
          },
          {
            id: '2',
            text: 'Its mass number rises by two.',
          },
          {
            id: '3',
            text: 'It becomes a different isotope with the same charge.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Adding electrons changes charge, not the proton count or the nuclear mass number.',
        sectionIds: ['particles', 'ions'],
        evidence:
          'Adding electrons changes charge, not the proton count or the nuclear mass number.',
      },
      {
        id: 'b3-atom-6',
        prompt:
          'Species X has 6 protons and 8 neutrons; species Y has 7 protons and 7 neutrons. Which comparison is correct?',
        options: [
          {
            id: '0',
            text: 'Same element, different isotopes',
          },
          {
            id: '1',
            text: 'Same atomic number, different mass numbers',
          },
          {
            id: '2',
            text: 'Same mass number, different elements',
          },
          {
            id: '3',
            text: 'Same proton count, different charges',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Both nuclei contain 14 nucleons, but their proton counts—and therefore elements—differ.',
        sectionIds: ['particles', 'isotopes'],
        evidence:
          'Both nuclei contain 14 nucleons, but their proton counts—and therefore elements—differ.',
      },
      {
        id: 'b3-atom-7',
        prompt:
          'In a simplified sample, 80% of atoms have mass 10 u and 20% have mass 11 u. What is the average atomic mass?',
        options: [
          {
            id: '0',
            text: '10.5 u',
          },
          {
            id: '1',
            text: '10.2 u',
          },
          {
            id: '2',
            text: '10.8 u',
          },
          {
            id: '3',
            text: '21 u',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'The count-weighted average is 0.80 × 10 + 0.20 × 11 = 10.2 u.',
        sectionIds: ['isotopes', 'averages'],
        evidence: 'The count-weighted average is 0.80 × 10 + 0.20 × 11 = 10.2 u.',
      },
      {
        id: 'b3-atom-8',
        prompt:
          'A mixture contains only atoms of mass 10 u and 11 u, with an average of 10.2 u. What follows about a randomly selected individual atom?',
        options: [
          {
            id: '0',
            text: 'It must have mass exactly 10.2 u.',
          },
          {
            id: '1',
            text: 'It has 10.2 protons on average within its own nucleus.',
          },
          {
            id: '2',
            text: 'Its mass changes continuously between 10 and 11 u.',
          },
          {
            id: '3',
            text: 'It has one of the two isotope masses, not necessarily the average.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'An average over a mixture need not be the mass of any individual member.',
        sectionIds: ['isotopes', 'averages'],
        evidence: 'An average over a mixture need not be the mass of any individual member.',
      },
      {
        id: 'b3-atom-9',
        prompt: 'Which change would necessarily change an atom’s element identity?',
        options: [
          {
            id: '0',
            text: 'Removing one electron',
          },
          {
            id: '1',
            text: 'Adding one neutron',
          },
          {
            id: '2',
            text: 'Changing its speed without changing its particles',
          },
          {
            id: '3',
            text: 'Changing the number of protons',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Atomic number, the proton count, defines the element; charge and neutron changes do not.',
        sectionIds: ['particles', 'isotopes', 'ions'],
        evidence:
          'Atomic number, the proton count, defines the element; charge and neutron changes do not.',
      },
      {
        id: 'b3-atom-10',
        prompt:
          'A sample’s heavier isotope becomes more abundant while each isotope’s mass stays fixed. What happens to the sample’s average atomic mass?',
        options: [
          {
            id: '0',
            text: 'It increases toward the heavier isotope’s mass.',
          },
          {
            id: '1',
            text: 'It must remain fixed because the element is unchanged.',
          },
          {
            id: '2',
            text: 'It decreases toward the lighter isotope’s mass.',
          },
          {
            id: '3',
            text: 'It becomes the simple unweighted mean regardless of abundance.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'A weighted average shifts toward the value receiving more weight.',
        sectionIds: ['averages'],
        evidence: 'A weighted average shifts toward the value receiving more weight.',
      },
    ],
  },
  {
    id: 'foundations-ecosystems',
    category: 'foundations',
    title: 'Energy through ecosystems',
    topic: 'Food webs, energy budgets and ecological pyramids',
    difficulty: 'Challenging',
    reading: {
      title: 'Follow energy without losing track of matter',
      sources: [
        {
          title: 'OpenStax Biology 2e · Energy Flow through Ecosystems (CC BY 4.0)',
          url: 'https://openstax.org/books/biology-2e/pages/46-2-energy-flow-through-ecosystems',
        },
      ],
      sections: [
        {
          id: 'web',
          title: 'Read a food web as transfers',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A food web represents feeding relationships among organisms. In the convention used here, an arrow points from a food resource to the organism consuming it: grass → rabbit means material and chemical energy pass from grass to rabbit. The arrow is not pointing toward the organism doing the hunting. Establishing this convention first prevents the entire web from being read backward.',
            },
            {
              kind: 'paragraph',
              text: 'Primary producers build organic compounds using inorganic carbon. Plants and many algae use sunlight as their energy source; some microbes use energy from chemical reactions instead. Consumers obtain organic material by eating organisms or their products. A primary consumer eats producers, while a secondary consumer eats primary consumers. These are roles in a feeding path, not permanent labels guaranteed by an animal’s name.',
            },
            {
              kind: 'table',
              caption: 'A simple feeding path',
              headers: ['Organism', 'Role in this path', 'Energy source'],
              rows: [
                ['Grass', 'Primary producer', 'Sunlight'],
                ['Grasshopper', 'Primary consumer', 'Grass'],
                ['Frog', 'Secondary consumer', 'Grasshopper'],
                ['Snake', 'Tertiary consumer', 'Frog'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Real webs contain branching paths. An omnivore may eat both plants and herbivores and therefore feed at more than one trophic level. A decomposer obtains material from dead organisms or waste from several levels; it is not simply the final predator in one chain. A diagram that includes only living prey-predator links may hide a large flow into detritus, the pool of dead organic material.',
            },
          ],
        },
        {
          id: 'budget',
          title: 'Separate energy captured from energy retained',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Producers use some of the energy they capture to maintain themselves. Gross primary production describes the rate at which producers capture energy in organic material; net primary production subtracts their own respiration costs. The remainder is new producer biomass available for growth and, potentially, consumers and decomposers. Respiration is not limited to animals: plants also use organic compounds to support their cellular work.',
            },
            {
              kind: 'equation',
              label: 'Producer budget',
              lines: [
                {
                  left: 'net primary production',
                  right: 'gross primary production − producer respiration',
                  reason: 'Compare rates measured over the same area and time interval.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A separate energy account',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose producers capture 1,200 energy units per square meter per year and use 700 in respiration. Net production is 500 in the same units. If herbivore production is 50, the ratio of herbivore production to producer net production is 50/500 = 0.10, or 10%. This denominator answers how much producer net production appears as new herbivore biomass, not how much sunlight originally reached the ecosystem.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Transfer between feeding levels is incomplete. Some material is not eaten, some is not digested, and much of the assimilated energy supports respiration rather than becoming new biomass. These destinations differ. Uneaten tissue and waste may support decomposers, while energy dispersed as heat is no longer available to the food web in the same concentrated chemical form. Energy is not destroyed; its form and availability change.',
            },
            {
              kind: 'paragraph',
              text: 'A transfer percentage depends on the organisms, conditions and precise denominator. Ten percent is a useful classroom assumption when supplied, not an exact universal constant. Use the stated efficiency for each step. If a problem supplies different efficiencies at successive steps, replacing them all with ten percent discards information.',
            },
          ],
        },
        {
          id: 'pyramids',
          title: 'Distinguish a stock from a flow',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Biomass is an amount present at a time, such as grams of living material per square meter. Production is a rate of generating new biomass or its energy content, such as energy per square meter per year. A pond can have little standing phytoplankton biomass while those producers reproduce quickly enough to support a larger standing mass of consumers. A photograph of the stock does not reveal how much passed through over a year.',
            },
            {
              kind: 'example',
              title: 'Fast replacement versus a large stock',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Imagine a producer population averaging 2 grams of biomass that replaces that amount every day. Its throughput can be much greater over a month than that of a 10-gram population growing very slowly. This does not make energy transfer perfectly efficient; it means the stock at one instant and the production over an interval answer different questions.',
                },
              ],
            },
            {
              kind: 'table',
              caption: 'Choose the right pyramid',
              headers: ['Quantity', 'Meaning', 'Can its shape invert?'],
              rows: [
                ['Number', 'Count of organisms', 'Yes: one tree can support many insects'],
                [
                  'Biomass',
                  'Standing mass at a time',
                  'Yes: rapid producer turnover can support larger consumer stocks',
                ],
                [
                  'Production energy',
                  'Energy in new biomass over a comparable interval',
                  'Not along the same closed feeding path with transfer losses',
                ],
              ],
            },
            {
              kind: 'paragraph',
              text: 'An energy pyramid compares production over a consistent area and interval. Under a defined feeding path, each receiving level incorporates less energy than the resource level provides. Apparent contradictions often involve changing the boundary: mobile consumers may import food from elsewhere, or estimates may compare different periods. Before interpreting a pyramid, ask what is measured, where its system boundary lies and which time interval is used.',
            },
          ],
        },
        {
          id: 'cycles',
          title: 'Matter can cycle while energy flows',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Carbon, nitrogen and other chemical elements can move repeatedly between organisms and their environment. Decomposers release nutrients that producers may use again. The energy that powered these transformations does not complete the same reusable cycle: substantial energy disperses as heat at every stage, so ecosystems need continuing energy input, usually sunlight or chemical energy from outside the local food web.',
            },
            {
              kind: 'paragraph',
              text: 'This distinction also helps explain why adding decomposers cannot turn an ecosystem into a perpetual energy source. Decomposition matters enormously for nutrient availability, but decomposers respire too. Restoring matter to usable chemical forms and restoring all the original energy to usable food are not equivalent operations.',
            },
            {
              kind: 'example',
              title: 'Two ledgers for one fallen leaf',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'The leaf’s carbon atoms can enter decomposers, carbon dioxide and later new plant tissue. Meanwhile, some chemical energy in the leaf supports decomposer metabolism and becomes dispersed heat. Following the carbon back into a plant does not show that the same usable energy has returned with it.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Food-web predictions should also respect indirect effects. Removing a predator may let its prey increase, which can change pressure on producers, but real outcomes depend on alternative food sources and other limits. A simplified chain can support a conditional prediction, not a universal guarantee for every ecosystem. For numerical questions, write each transfer as a multiplier, retain units and apply the multipliers in feeding order.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b3-eco-1',
        prompt:
          'Using food → consumer arrows, which chain correctly represents grass eaten by rabbits that are eaten by foxes?',
        options: [
          {
            id: '0',
            text: 'Fox → rabbit → grass',
          },
          {
            id: '1',
            text: 'Grass → rabbit → fox',
          },
          {
            id: '2',
            text: 'Rabbit → grass → fox',
          },
          {
            id: '3',
            text: 'Grass → fox → rabbit',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The arrows follow material and energy from the food source to the organism consuming it.',
        sectionIds: ['web'],
        evidence:
          'The arrows follow material and energy from the food source to the organism consuming it.',
      },
      {
        id: 'b3-eco-2',
        prompt:
          'An animal eats both seeds and herbivorous insects. Which description best fits its role?',
        options: [
          {
            id: '0',
            text: 'It must be only a primary consumer.',
          },
          {
            id: '1',
            text: 'It must be a primary producer.',
          },
          {
            id: '2',
            text: 'It cannot be represented in a food web.',
          },
          {
            id: '3',
            text: 'It can feed at more than one trophic level.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Feeding levels describe paths: seeds and herbivorous insects place the animal at different consumer levels.',
        sectionIds: ['web'],
        evidence:
          'Feeding levels describe paths: seeds and herbivorous insects place the animal at different consumer levels.',
      },
      {
        id: 'b3-eco-3',
        prompt:
          'Producers capture 900 energy units per area per year and use 350 in respiration. What is their net primary production?',
        options: [
          {
            id: '0',
            text: '550 in the same units',
          },
          {
            id: '1',
            text: '1,250 in the same units',
          },
          {
            id: '2',
            text: '350 in the same units',
          },
          {
            id: '3',
            text: '900 in the same units',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Net production subtracts producer respiration from gross captured production: 900 − 350 = 550.',
        sectionIds: ['budget'],
        evidence:
          'Net production subtracts producer respiration from gross captured production: 900 − 350 = 550.',
      },
      {
        id: 'b3-eco-4',
        prompt: 'Why is “plants do not respire because they photosynthesize” incorrect?',
        options: [
          {
            id: '0',
            text: 'Photosynthesis occurs only in animals.',
          },
          {
            id: '1',
            text: 'Plants consume no energy during growth.',
          },
          {
            id: '2',
            text: 'Plants also use organic compounds in respiration to support cellular work.',
          },
          {
            id: '3',
            text: 'Respiration and photosynthesis are names for exactly the same process.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Photosynthesis captures energy into organic material; plants also respire and use some of that material.',
        sectionIds: ['web', 'budget'],
        evidence:
          'Photosynthesis captures energy into organic material; plants also respire and use some of that material.',
      },
      {
        id: 'b3-eco-5',
        prompt:
          'A model supplies producer net production of 2,000 units, with 10% transferred into herbivore production and 20% of that into carnivore production. What is carnivore production?',
        options: [
          {
            id: '0',
            text: '400 units',
          },
          {
            id: '1',
            text: '200 units',
          },
          {
            id: '2',
            text: '40 units',
          },
          {
            id: '3',
            text: '600 units',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'Apply the successive transfer fractions: 2,000 × 0.10 × 0.20 = 40.',
        sectionIds: ['budget'],
        evidence: 'Apply the successive transfer fractions: 2,000 × 0.10 × 0.20 = 40.',
      },
      {
        id: 'b3-eco-6',
        prompt:
          'A lake has less standing producer biomass than consumer biomass. Which explanation can make this compatible with energy losses between levels?',
        options: [
          {
            id: '0',
            text: 'The producers reproduce rapidly and are consumed rapidly.',
          },
          {
            id: '1',
            text: 'Consumers create their own energy from nothing.',
          },
          {
            id: '2',
            text: 'Transfer efficiency must exceed 100%.',
          },
          {
            id: '3',
            text: 'Standing biomass and annual production are identical measurements.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Rapid turnover can support consumers despite a small producer stock at a particular instant.',
        sectionIds: ['budget', 'pyramids'],
        evidence:
          'Rapid turnover can support consumers despite a small producer stock at a particular instant.',
      },
      {
        id: 'b3-eco-7',
        prompt: 'Which unit describes a flow rate rather than a standing biomass stock?',
        options: [
          {
            id: '0',
            text: 'Grams of algae per square meter at noon',
          },
          {
            id: '1',
            text: 'Number of trees currently in a hectare',
          },
          {
            id: '2',
            text: 'Kilograms of fish present in a pond',
          },
          {
            id: '3',
            text: 'Kilojoules per square meter per year',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Energy per area per time measures a production flow; the other options describe amounts present.',
        sectionIds: ['pyramids'],
        evidence:
          'Energy per area per time measures a production flow; the other options describe amounts present.',
      },
      {
        id: 'b3-eco-8',
        prompt:
          'What is the key difference between nutrient recycling and energy flow in an ecosystem?',
        options: [
          {
            id: '0',
            text: 'Neither matter nor energy leaves organisms.',
          },
          {
            id: '1',
            text: 'Nutrient atoms can be reused while usable energy is continually dispersed as heat.',
          },
          {
            id: '2',
            text: 'Decomposers restore all energy as food without losses.',
          },
          {
            id: '3',
            text: 'Only consumers contain matter, while producers contain energy.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Matter can cycle through organisms and the environment; energy transfers disperse heat and require continuing input.',
        sectionIds: ['budget', 'cycles'],
        evidence:
          'Matter can cycle through organisms and the environment; energy transfers disperse heat and require continuing input.',
      },
      {
        id: 'b3-eco-9',
        prompt:
          'A single tree supports thousands of leaf-eating insects. Which ecological pyramid can therefore be inverted at these levels?',
        options: [
          {
            id: '0',
            text: 'A pyramid of organism numbers',
          },
          {
            id: '1',
            text: 'An energy-production pyramid solely because insect counts are larger',
          },
          {
            id: '2',
            text: 'Every possible pyramid, regardless of what is measured',
          },
          {
            id: '3',
            text: 'A pyramid of atomic numbers',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'A count pyramid can have more consumers than producers when the producers are much larger organisms.',
        sectionIds: ['web', 'pyramids'],
        evidence:
          'A count pyramid can have more consumers than producers when the producers are much larger organisms.',
      },
      {
        id: 'b3-eco-10',
        prompt: 'Which statement about the classroom “10% transfer rule” is most accurate?',
        options: [
          {
            id: '0',
            text: 'It is exactly true for every feeding link.',
          },
          {
            id: '1',
            text: 'It states that the remaining energy is destroyed.',
          },
          {
            id: '2',
            text: 'It is an approximate model; use specific efficiencies when provided.',
          },
          {
            id: '3',
            text: 'It applies only to numbers of organisms, not energy.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Energy-transfer efficiencies vary, and stated problem-specific values should govern the calculation.',
        sectionIds: ['budget'],
        evidence:
          'Energy-transfer efficiencies vary, and stated problem-specific values should govern the calculation.',
      },
    ],
  },
  {
    id: 'foundations-light',
    category: 'foundations',
    title: 'Light and the electromagnetic spectrum',
    topic: 'Wavelength, frequency, photon energy and observation',
    difficulty: 'Challenging',
    reading: {
      title: 'Three measurements of the same light',
      sources: [
        {
          title: 'NASA Goddard · The Electromagnetic Spectrum',
          url: 'https://imagine.gsfc.nasa.gov/science/toolbox/emspectrum1.html',
        },
        {
          title: 'NASA Goddard · Wavelength, frequency and energy',
          url: 'https://imagine.gsfc.nasa.gov/science/toolbox/emspectrum2.html',
        },
      ],
      sections: [
        {
          id: 'waves',
          title: 'Separate a distance from a rate',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A repeating wave pattern has a wavelength: the distance between successive points at the same phase, such as one crest and the next. Frequency measures how many cycles pass a fixed point each second. Wavelength is a length, while frequency is a rate. A long wavelength is not a long time unless a propagation speed has also been specified.',
            },
            {
              kind: 'paragraph',
              text: 'If five crests pass a marker each second and neighboring crests are two meters apart, the pattern advances ten meters in a second. This counting argument gives speed = wavelength × frequency. Frequency is measured in hertz, abbreviated Hz, meaning cycles per second. The cycle count is dimensionless, so multiplying meters per cycle by cycles per second yields meters per second.',
            },
            {
              kind: 'equation',
              label: 'Wave relationship',
              lines: [
                {
                  left: 'v',
                  right: 'λ × f',
                  reason: 'v is propagation speed, λ is wavelength and f is frequency.',
                },
              ],
            },
            {
              kind: 'table',
              caption: 'Keep the quantities distinct',
              headers: ['Quantity', 'What it measures', 'Typical unit'],
              rows: [
                ['Wavelength λ', 'Distance per repeating cycle', 'meter'],
                ['Frequency f', 'Cycles passing per time', 'hertz'],
                ['Speed v', 'Distance the pattern travels per time', 'meter per second'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Electromagnetic radiation includes radio waves, visible light and X-rays. In vacuum all travel at the same speed c, about 3 × 10⁸ meters per second. At that fixed speed, increasing frequency requires decreasing wavelength. A higher-frequency wave does not race ahead of a lower-frequency wave through vacuum. In material media, propagation and refraction require additional care; the questions specifying vacuum avoid that complication.',
            },
            {
              kind: 'diagram',
              drawing: 'wave-frequency',
              caption: 'At the same speed, shorter wavelengths mean more cycles pass each second.',
              description:
                'Two waves occupy the same horizontal distance. The upper wave completes two cycles and the lower wave four. At equal propagation speed the lower wave has half the wavelength and twice the frequency.',
            },
          ],
        },
        {
          id: 'spectrum',
          title: 'Visible light occupies a small part of the spectrum',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The electromagnetic spectrum organizes radiation by wavelength or frequency. From lower toward higher frequency, the familiar regions are radio, microwave, infrared, visible, ultraviolet, X-ray and gamma ray. These labels divide a continuous range rather than naming unrelated physical substances. Region boundaries are conventions and may depend on context, so exact boundary numbers are not the focus here.',
            },
            {
              kind: 'paragraph',
              text: 'Within visible light, red has a longer wavelength and lower frequency than violet. Infrared lies beyond the red end in the longer-wavelength direction; ultraviolet lies beyond violet in the shorter-wavelength direction. Radiation invisible to human eyes is still electromagnetic radiation. An instrument sensitive to infrared or radio can collect information an unaided eye cannot see.',
            },
            {
              kind: 'example',
              title: 'Convert a radio wavelength',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'For a radio wave in vacuum with wavelength 3 meters, f = c/λ = (3 × 10⁸)/3 = 1 × 10⁸ Hz. That is 100 million cycles per second, or 100 MHz. If the wavelength doubles to 6 meters while the medium stays vacuum, the frequency halves to 50 MHz.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Units matter before substituting into the equation. One nanometer is 10⁻⁹ meters and one megahertz is 10⁶ hertz. Treating nanometers as meters introduces a billion-fold error even if the algebraic rearrangement is correct. A useful qualitative check is that a shorter wavelength must correspond to a larger frequency when the speed is held fixed.',
            },
          ],
        },
        {
          id: 'photons',
          title: 'Energy per photon differs from total beam power',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Electromagnetic radiation exchanges energy in photons. The energy of one photon is proportional to its frequency: E = hf, where h is Planck’s constant. Combining this relationship with c = λf gives E = hc/λ in vacuum. Higher frequency therefore means more energy per photon, while longer wavelength means less. These are statements about individual quanta, not automatically about how much energy a whole lamp emits each second.',
            },
            {
              kind: 'equation',
              label: 'Photon energy',
              lines: [
                {
                  left: 'E',
                  right: 'h × f',
                  reason:
                    'h is a constant; changing photon frequency changes the energy of each photon.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Two beams with different photon counts',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose beam A delivers 100 photons per second with energy 2 units each. Its power is 200 energy units per second. Beam B delivers 500 photons per second with energy 1 unit each, giving power 500. B has lower energy per photon but greater total power. Both photon energy and the number arriving per second matter.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Thus increasing the intensity of a single-frequency beam can deliver more energy without increasing that frequency. Confusing intensity with photon energy leads to claims such as every bright red lamp having higher-energy photons than any dim blue lamp. A comparison of individual red and blue photons depends on frequency; a comparison of total power also needs the rate of photon arrival.',
            },
            {
              kind: 'paragraph',
              text: 'The spectrum alone is also not a complete measure of biological effect or safety. Dose, exposure duration and interactions with matter matter too. This unit compares wave quantities and observational tools; it does not turn a region label into a general exposure rule.',
            },
          ],
        },
        {
          id: 'observe',
          title: 'What reaches a detector depends on the path',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A telescope can record only radiation that reaches it and interacts with its detector. Earth’s atmosphere transmits some wavelength ranges better than others. Visible light and portions of the radio spectrum reach the surface well, while much radiation in other regions is absorbed before reaching ground-based instruments. This is one reason astronomers place some observatories above the atmosphere.',
            },
            {
              kind: 'paragraph',
              text: 'An orbiting telescope does not make distant radiation travel faster, nor does it make all instruments equally sensitive to every wavelength. It changes the intervening material and observing environment. Different detectors and optical designs are still needed for different parts of the spectrum. Combining observations can reveal complementary features of the same object rather than forcing every observation into visible colors.',
            },
            {
              kind: 'example',
              title: 'A missing ground signal',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose a source emits strongly in a band the atmosphere absorbs. A ground detector’s weak reading cannot by itself establish that the source emits little in that band. The path may have removed the signal. Compare a measurement above the absorbing layer or use a band with different transmission to separate source behavior from the observing conditions.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The same reasoning applies to everyday instruments: a detector’s absence of a signal is informative only when the signal could have reached and been registered by it. Keep source emission, propagation through material and detector response as separate stages of the explanation.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b3-light-1',
        prompt:
          'Two electromagnetic waves travel through vacuum. One has twice the frequency of the other. What is its wavelength?',
        options: [
          {
            id: '0',
            text: 'Twice as long',
          },
          {
            id: '1',
            text: 'Half as long',
          },
          {
            id: '2',
            text: 'The same length',
          },
          {
            id: '3',
            text: 'Four times as long',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'In vacuum c = λf is fixed, so doubling frequency halves wavelength.',
        sectionIds: ['waves'],
        evidence: 'In vacuum c = λf is fixed, so doubling frequency halves wavelength.',
      },
      {
        id: 'b3-light-2',
        prompt: 'Which pair of units correctly describes wavelength and frequency, respectively?',
        options: [
          {
            id: '0',
            text: 'Seconds and meters',
          },
          {
            id: '1',
            text: 'Hertz and joules',
          },
          {
            id: '2',
            text: 'Meters and hertz',
          },
          {
            id: '3',
            text: 'Joules and meters per second',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Wavelength is a distance and frequency is cycles per second, measured in hertz.',
        sectionIds: ['waves'],
        evidence: 'Wavelength is a distance and frequency is cycles per second, measured in hertz.',
      },
      {
        id: 'b3-light-3',
        prompt:
          'Compared with a red-light photon, a violet-light photon in vacuum has which combination?',
        options: [
          {
            id: '0',
            text: 'Shorter wavelength and greater energy',
          },
          {
            id: '1',
            text: 'Longer wavelength and greater energy',
          },
          {
            id: '2',
            text: 'Shorter wavelength and lower energy',
          },
          {
            id: '3',
            text: 'The same wavelength and a different speed',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Violet has higher frequency than red; c = λf and E = hf imply shorter wavelength and higher photon energy.',
        sectionIds: ['waves', 'spectrum', 'photons'],
        evidence:
          'Violet has higher frequency than red; c = λf and E = hf imply shorter wavelength and higher photon energy.',
      },
      {
        id: 'b3-light-4',
        prompt:
          'A beam’s frequency stays fixed while twice as many photons arrive each second. What changes?',
        options: [
          {
            id: '0',
            text: 'Each photon’s energy doubles.',
          },
          {
            id: '1',
            text: 'The wavelength halves.',
          },
          {
            id: '2',
            text: 'The vacuum propagation speed doubles.',
          },
          {
            id: '3',
            text: 'The beam’s delivered power doubles.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'At fixed frequency, energy per photon stays fixed; doubling their arrival rate doubles energy per second.',
        sectionIds: ['waves', 'photons'],
        evidence:
          'At fixed frequency, energy per photon stays fixed; doubling their arrival rate doubles energy per second.',
      },
      {
        id: 'b3-light-5',
        prompt:
          'Using c = 3 × 10⁸ m/s, what is the frequency of a wave with vacuum wavelength 2 meters?',
        options: [
          {
            id: '0',
            text: '1.5 × 10⁸ Hz',
          },
          {
            id: '1',
            text: '6 × 10⁸ Hz',
          },
          {
            id: '2',
            text: '2 × 10⁸ Hz',
          },
          {
            id: '3',
            text: '1.5 × 10⁻⁸ Hz',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Frequency is c/λ = (3 × 10⁸)/2 = 1.5 × 10⁸ Hz.',
        sectionIds: ['waves', 'spectrum'],
        evidence: 'Frequency is c/λ = (3 × 10⁸)/2 = 1.5 × 10⁸ Hz.',
      },
      {
        id: 'b3-light-6',
        prompt:
          'Why can an orbiting observatory detect some bands more effectively than a ground observatory?',
        options: [
          {
            id: '0',
            text: 'Vacuum increases all photon frequencies.',
          },
          {
            id: '1',
            text: 'It avoids absorption by much of Earth’s atmosphere.',
          },
          {
            id: '2',
            text: 'The source emits only when a satellite is present.',
          },
          {
            id: '3',
            text: 'All orbiting detectors respond equally to all wavelengths.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Getting above the atmosphere avoids an absorbing layer, although detector sensitivity still matters.',
        sectionIds: ['observe'],
        evidence:
          'Getting above the atmosphere avoids an absorbing layer, although detector sensitivity still matters.',
      },
      {
        id: 'b3-light-7',
        prompt: 'Which statement correctly compares radio waves and visible light in vacuum?',
        options: [
          {
            id: '0',
            text: 'Radio waves travel slower because their wavelengths are longer.',
          },
          {
            id: '1',
            text: 'Visible light is electromagnetic but radio is not.',
          },
          {
            id: '2',
            text: 'Their speeds are proportional to their photon energies.',
          },
          {
            id: '3',
            text: 'They are both electromagnetic radiation and travel at the same speed.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Different spectrum regions have different frequencies and wavelengths but the same propagation speed in vacuum.',
        sectionIds: ['waves', 'spectrum'],
        evidence:
          'Different spectrum regions have different frequencies and wavelengths but the same propagation speed in vacuum.',
      },
      {
        id: 'b3-light-8',
        prompt:
          'A source’s radiation is strongly absorbed before reaching a detector. What does a weak detector reading establish by itself?',
        options: [
          {
            id: '0',
            text: 'The source must emit little radiation.',
          },
          {
            id: '1',
            text: 'The radiation is not electromagnetic.',
          },
          {
            id: '2',
            text: 'It does not distinguish weak emission from strong absorption.',
          },
          {
            id: '3',
            text: 'The detector has measured the source’s full emitted power.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'The signal depends on both source emission and transmission along the path.',
        sectionIds: ['observe'],
        evidence: 'The signal depends on both source emission and transmission along the path.',
      },
      {
        id: 'b3-light-9',
        prompt:
          'Which region lies immediately beyond visible red in the longer-wavelength direction?',
        options: [
          {
            id: '0',
            text: 'Ultraviolet',
          },
          {
            id: '1',
            text: 'X-ray',
          },
          {
            id: '2',
            text: 'Infrared',
          },
          {
            id: '3',
            text: 'Gamma ray',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Infrared has longer wavelengths than visible red; ultraviolet lies beyond violet in the opposite direction.',
        sectionIds: ['spectrum'],
        evidence:
          'Infrared has longer wavelengths than visible red; ultraviolet lies beyond violet in the opposite direction.',
      },
      {
        id: 'b3-light-10',
        prompt:
          'Beam A has higher energy per photon than beam B. What additional information is needed to compare their total powers?',
        options: [
          {
            id: '0',
            text: 'How many photons each delivers per unit time',
          },
          {
            id: '1',
            text: 'The color names alone',
          },
          {
            id: '2',
            text: 'Whether both beams are called radiation',
          },
          {
            id: '3',
            text: 'Only the vacuum speed of light',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Power depends on energy per photon multiplied by photon arrival rate.',
        sectionIds: ['waves', 'photons'],
        evidence: 'Power depends on energy per photon multiplied by photon arrival rate.',
      },
    ],
  },
  {
    id: 'foundations-industrial-city',
    category: 'foundations',
    title: 'The industrial city',
    topic: 'US urban growth, infrastructure and reform in the late nineteenth century',
    difficulty: 'Challenging',
    reading: {
      title: 'How work, transport and housing changed a city',
      sources: [
        {
          title: 'OpenStax US History · Urbanization and Its Challenges (CC BY 4.0)',
          url: 'https://openstax.org/books/us-history/pages/19-1-urbanization-and-its-challenges',
        },
      ],
      sections: [
        {
          id: 'growth',
          title: 'Industrialization and urbanization are related, not identical',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Industrialization is a change in production: machinery, energy sources and organized factory labor expand the scale and kinds of goods that can be made. Urbanization is a change in settlement: a growing share of people live in towns and cities. They often reinforce each other, but the words describe different processes. A factory can open outside a major city, and a port or administrative center can grow for reasons beyond factory production.',
            },
            {
              kind: 'paragraph',
              text: 'In the late nineteenth-century United States, expanding industrial employment drew workers toward urban centers. Some moved from rural areas; others immigrated from abroad. A push factor makes leaving an origin more attractive or necessary, such as poor prospects or persecution. A pull factor attracts people to a destination, such as wage employment or an existing community network. One migration can reflect several of these factors simultaneously.',
            },
            {
              kind: 'table',
              caption: 'Separate the mechanisms',
              headers: ['Observation', 'Process illustrated'],
              rows: [
                ['Machines expand factory output', 'Industrialization'],
                ['A larger population share lives in cities', 'Urbanization'],
                ['Limited rural prospects encourage departure', 'Push factor'],
                ['Factory jobs attract newcomers', 'Pull factor'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Job availability did not guarantee comfortable living or safe employment. Long shifts, low wages and dangerous work could coexist with an expanding labor market. People compare available alternatives under constraints; a decision to move does not prove the destination had no hardship. This distinction helps explain why growing cities could be both places of opportunity and centers of severe inequality.',
            },
          ],
        },
        {
          id: 'infrastructure',
          title: 'Technologies change different constraints',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Factories driven directly by water power depended strongly on suitable water sites. Steam power loosened that particular location constraint, although factories still needed fuel, water for their operations, transport and workers. Saying a technology reduces one dependency is more accurate than saying it removes every geographic limitation. Industrial locations remained shaped by costs and connections.',
            },
            {
              kind: 'paragraph',
              text: 'Electric lighting allowed work and street activity to continue after sunset. Communication networks accelerated coordination over distance. Urban transit changed how far workers could reasonably live from jobs. These effects should not be treated as interchangeable: a telephone can move information quickly without moving a commuter, and a brighter factory does not by itself solve housing scarcity.',
            },
            {
              kind: 'example',
              title: 'Choose the intervention for the bottleneck',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A neighborhood has jobs across town but a slow journey to reach them. Better transit directly changes commuting access. If instead a workshop cannot operate after dark, dependable lighting addresses that constraint. Both investments may support growth, but their immediate mechanisms differ; a historical explanation should identify the intervening steps.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'As street traffic grew, elevated and underground railways offered routes separated from crowded street surfaces. These systems required large investments and did not erase congestion everywhere. Their importance lies in changing transport capacity and the use of limited street space, rather than in a claim that one invention instantly perfected the city.',
            },
          ],
        },
        {
          id: 'buildings',
          title: 'Growing upward requires more than a tall structure',
          blocks: [
            {
              kind: 'paragraph',
              text: 'High central land prices and limited well-connected space made upward construction attractive. Stronger structural systems made taller buildings technically feasible, while elevators made their upper floors practically accessible. This is an example of complementary technologies: a building can be structurally possible yet commercially inconvenient if reaching its upper stories requires a long stair climb.',
            },
            {
              kind: 'example',
              title: 'Technical possibility versus routine use',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Imagine two equally strong tall office buildings. One has reliable passenger elevators and the other only stairs. The steel frame addresses whether the structure can stand; the elevator addresses whether many people can use its height conveniently. Neither feature alone explains the full adoption of tall buildings.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Housing for working-class residents faced a different but related pressure. Many workers needed to live near employment, while wages and the available housing stock constrained choice. Crowded tenements, poor ventilation and inadequate plumbing could result when demand and growth outpaced investment and regulation. Building more office floors did not automatically supply safe affordable homes.',
            },
            {
              kind: 'paragraph',
              text: 'Rapid growth also strained shared systems such as drinking-water supply, sewage disposal and fire protection. These are networks and public services, not problems a household can necessarily solve by working harder. A city may become more productive while basic services lag. Looking only at factory output or skyline height would miss whether the benefits and burdens were distributed evenly.',
            },
          ],
        },
        {
          id: 'reform',
          title: 'Document conditions, then distinguish kinds of response',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Journalists and reformers made urban living conditions visible to audiences who might not otherwise encounter them. Jacob Riis used reporting and photographs to publicize New York tenement conditions. Such material is evidence about conditions and also a constructed presentation: selection, framing and the author’s assumptions shape what viewers see. Recognizing those choices does not make photographs useless; it means interpreting them critically.',
            },
            {
              kind: 'paragraph',
              text: 'Settlement houses offered services such as classes, childcare and community support. Jane Addams’s Hull House in Chicago is a prominent example. Providing immediate help and advocating changes to housing or labor rules were connected but distinct activities. A meal or class assists people now; a policy change seeks to alter recurring conditions. Neither should be described as merely the other under a different name.',
            },
            {
              kind: 'table',
              caption: 'Responses work at different levels',
              headers: ['Response', 'Immediate target'],
              rows: [
                ['Document tenement conditions', 'Public knowledge and attention'],
                ['Offer childcare or evening classes', 'Household needs and access'],
                ['Improve sewer infrastructure', 'Shared environmental conditions'],
                ['Change housing or labor rules', 'Standards governing repeated practice'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'The term reform covers efforts to change conditions, but it should not imply that all reformers shared one program or that all residents agreed with them. Residents had their own preferences and forms of organization, and interventions could involve conflict as well as aid. The chapter concerns broad mechanisms in a particular period, not a claim that every city followed one timetable.',
            },
            {
              kind: 'paragraph',
              text: 'When evaluating a historical explanation, connect a cause to an intermediate mechanism and then to an outcome: expanded factory demand can draw workers; population growth can strain housing; inadequate housing can provoke documentation and political pressure. These links are more informative than a list of inventions and famous names, and they leave room for different local conditions and competing causes.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b3-city-1',
        prompt:
          'Which observation specifically describes urbanization rather than industrialization?',
        options: [
          {
            id: '0',
            text: 'A factory replaces hand tools with powered machines.',
          },
          {
            id: '1',
            text: 'A mill doubles its output per worker.',
          },
          {
            id: '2',
            text: 'A larger share of the population lives in cities.',
          },
          {
            id: '3',
            text: 'A new process reduces the cost of making steel.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Urbanization concerns settlement patterns; industrialization concerns production.',
        sectionIds: ['growth'],
        evidence:
          'Urbanization concerns settlement patterns; industrialization concerns production.',
      },
      {
        id: 'b3-city-2',
        prompt:
          'In an account of rural-to-urban migration, which is a pull factor at the destination?',
        options: [
          {
            id: '0',
            text: 'Available factory employment in the city',
          },
          {
            id: '1',
            text: 'A failed harvest at the rural origin',
          },
          {
            id: '2',
            text: 'Religious persecution at the origin',
          },
          {
            id: '3',
            text: 'Declining rural earnings at the origin',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'A pull factor attracts people toward a destination; the other choices are pressures to leave the origin.',
        sectionIds: ['growth'],
        evidence:
          'A pull factor attracts people toward a destination; the other choices are pressures to leave the origin.',
      },
      {
        id: 'b3-city-3',
        prompt:
          'What location constraint did steam power help loosen for factories formerly driven directly by water?',
        options: [
          {
            id: '0',
            text: 'All need for transport access',
          },
          {
            id: '1',
            text: 'All need for fuel supplies',
          },
          {
            id: '2',
            text: 'All need for nearby workers',
          },
          {
            id: '3',
            text: 'Dependence on a suitable flowing-water power site',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Steam reduced direct dependence on suitable water-power sites without eliminating other resource and transport needs.',
        sectionIds: ['infrastructure'],
        evidence:
          'Steam reduced direct dependence on suitable water-power sites without eliminating other resource and transport needs.',
      },
      {
        id: 'b3-city-4',
        prompt:
          'Why were elevators complementary to structural techniques that allowed taller buildings?',
        options: [
          {
            id: '0',
            text: 'They replaced the need for a supporting structure.',
          },
          {
            id: '1',
            text: 'They made upper floors routinely accessible to occupants.',
          },
          {
            id: '2',
            text: 'They guaranteed affordable housing for all nearby workers.',
          },
          {
            id: '3',
            text: 'They removed the value of central urban land.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Structural systems made height possible; elevators made that height convenient to use.',
        sectionIds: ['buildings'],
        evidence:
          'Structural systems made height possible; elevators made that height convenient to use.',
      },
      {
        id: 'b3-city-5',
        prompt: 'Which problem did elevated and underground rail routes most directly address?',
        options: [
          {
            id: '0',
            text: 'The absence of written employment contracts',
          },
          {
            id: '1',
            text: 'The need to illuminate factory interiors',
          },
          {
            id: '2',
            text: 'Competition for crowded street space during urban travel',
          },
          {
            id: '3',
            text: 'The lack of strong building frames',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Moving rail traffic above or below street level separated it from surface congestion.',
        sectionIds: ['infrastructure'],
        evidence:
          'Moving rail traffic above or below street level separated it from surface congestion.',
      },
      {
        id: 'b3-city-6',
        prompt:
          'Why does a rapid rise in factory jobs not prove that workers enjoyed good living conditions?',
        options: [
          {
            id: '0',
            text: 'Employment growth can coexist with low wages, crowding and inadequate services.',
          },
          {
            id: '1',
            text: 'People never move toward opportunities.',
          },
          {
            id: '2',
            text: 'Urbanization means all residents become wealthier at the same rate.',
          },
          {
            id: '3',
            text: 'A city cannot have both factories and tenements.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Opportunity relative to alternatives does not guarantee safe work or adequate urban housing and infrastructure.',
        sectionIds: ['growth', 'buildings'],
        evidence:
          'Opportunity relative to alternatives does not guarantee safe work or adequate urban housing and infrastructure.',
      },
      {
        id: 'b3-city-7',
        prompt: 'What was a central role of settlement houses such as Hull House?',
        options: [
          {
            id: '0',
            text: 'Replacing every elected city institution',
          },
          {
            id: '1',
            text: 'Manufacturing elevators for new office towers',
          },
          {
            id: '2',
            text: 'Providing services and support within urban communities',
          },
          {
            id: '3',
            text: 'Preventing residents from attending classes',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Settlement houses provided practical community services and often connected that work with broader reform.',
        sectionIds: ['reform'],
        evidence:
          'Settlement houses provided practical community services and often connected that work with broader reform.',
      },
      {
        id: 'b3-city-8',
        prompt: 'Which comparison distinguishes immediate assistance from structural reform?',
        options: [
          {
            id: '0',
            text: 'A photograph and a newspaper article are both transport systems.',
          },
          {
            id: '1',
            text: 'Childcare helps households directly; housing-law changes alter recurring standards.',
          },
          {
            id: '2',
            text: 'Sewer construction changes only an individual’s opinion.',
          },
          {
            id: '3',
            text: 'A labor rule and a meal are the same kind of intervention.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Direct services meet present needs, while rule changes aim to alter conditions that recur.',
        sectionIds: ['reform'],
        evidence:
          'Direct services meet present needs, while rule changes aim to alter conditions that recur.',
      },
      {
        id: 'b3-city-9',
        prompt: 'How should a historian use reform-era photographs of tenements?',
        options: [
          {
            id: '0',
            text: 'As complete, unselected records of every city resident',
          },
          {
            id: '1',
            text: 'As worthless because photographers had viewpoints',
          },
          {
            id: '2',
            text: 'As proof that every city had identical conditions',
          },
          {
            id: '3',
            text: 'As evidence interpreted with attention to selection, framing and context',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Photographs document selected scenes; critical interpretation considers both what they show and how they were made.',
        sectionIds: ['reform'],
        evidence:
          'Photographs document selected scenes; critical interpretation considers both what they show and how they were made.',
      },
      {
        id: 'b3-city-10',
        prompt:
          'A factory district grows faster than water and sewage systems are expanded. What best explains the resulting strain?',
        options: [
          {
            id: '0',
            text: 'Population and production can outpace shared infrastructure.',
          },
          {
            id: '1',
            text: 'Industrialization automatically supplies every public service.',
          },
          {
            id: '2',
            text: 'Communication improvements eliminate sewage requirements.',
          },
          {
            id: '3',
            text: 'More jobs necessarily reduce the population using water.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Shared systems require investment and capacity; growth in work and population does not automatically provide them.',
        sectionIds: ['buildings'],
        evidence:
          'Shared systems require investment and capacity; growth in work and population does not automatically provide them.',
      },
    ],
  },
  {
    id: 'foundations-coordinates',
    category: 'foundations',
    title: 'Coordinates, distances and slopes',
    topic: 'Rectangular coordinates, distance, midpoint and line interpretation',
    difficulty: 'Challenging',
    reading: {
      title: 'Turn positions into relationships',
      sources: [
        {
          title:
            'OpenStax Algebra and Trigonometry 2e · Rectangular Coordinate Systems and Graphs (CC BY 4.0)',
          url: 'https://openstax.org/books/algebra-and-trigonometry-2e/pages/2-1-the-rectangular-coordinate-systems-and-graphs',
        },
      ],
      sections: [
        {
          id: 'positions',
          title: 'An ordered pair is two instructions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A rectangular coordinate system uses two perpendicular number lines. The horizontal coordinate x measures position along one axis; the vertical coordinate y measures position along the other. Their intersection is the origin, (0,0). In the standard orientation, positive x points right and positive y points up. The order in (x,y) matters: (2,5) and (5,2) are usually different points.',
            },
            {
              kind: 'paragraph',
              text: 'Coordinates describe positions relative to the chosen origin and units. A negative coordinate is a direction, not a negative physical length. The point (−3,2) lies three units left and two units above the origin. Distance is nonnegative even when one or both endpoints have negative coordinates. Before calculating physical distances, confirm that the axes use compatible units and scales.',
            },
            {
              kind: 'example',
              title: 'Separate position and displacement',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Travel from (−2,1) to (3,4). The horizontal change is 3 − (−2) = 5 and the vertical change is 4 − 1 = 3. These differences describe the displacement. Adding the two x coordinates would not measure the change; it would mix two positions instead of subtracting the starting point from the ending point.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A translation moves every point by the same horizontal and vertical amounts. This changes coordinate labels but preserves differences between points, and therefore preserves distances and slopes. This is why moving a map’s origin does not change the physical route it depicts. Changing the scale of just one axis, however, can change apparent angles and lengths on the drawing.',
            },
          ],
        },
        {
          id: 'distance',
          title: 'Build a right triangle from coordinate changes',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Join two points by a horizontal segment and then a vertical segment. Those perpendicular legs form a right triangle whose hypotenuse is the straight segment between the original points. The Pythagorean theorem says the square of the hypotenuse length equals the sum of the squares of the two leg lengths. Using the coordinate differences for those legs gives the distance formula.',
            },
            {
              kind: 'equation',
              label: 'Straight-line distance',
              lines: [
                {
                  left: 'd',
                  right: '√((x₂ − x₁)² + (y₂ − y₁)²)',
                  reason:
                    'The subscripts distinguish the two endpoints; both axes use the same length unit.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A separate distance calculation',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Between (1,2) and (4,6), the coordinate changes are 3 and 4. The straight distance is √(3² + 4²) = √25 = 5. A route constrained to horizontal and vertical streets instead travels at least 3 + 4 = 7. Those are different distances because the allowed paths differ.',
                },
                {
                  kind: 'table',
                  caption: 'Two path rules',
                  headers: ['Allowed travel', 'Distance'],
                  rows: [
                    ['Straight between endpoints', '5 units'],
                    ['Only horizontal and vertical segments', '7 units'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'Subtracting the endpoints in the opposite order changes both signs but not their squares, so the straight distance is unchanged.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Squaring does not mean that the final distance is measured in square units. The sum inside the root has square-length units, and the square root returns ordinary length units. Forgetting the final root gives the squared distance. Adding the coordinate changes without squaring instead computes a street-grid distance in an appropriate model, not the straight Euclidean distance.',
            },
            {
              kind: 'diagram',
              drawing: 'coordinate-distance',
              caption: 'A straight segment and a street-grid route connect the same points.',
              description:
                'Points A at one, two and B at four, six form a right triangle with horizontal leg three and vertical leg four. The straight hypotenuse is five units, while travel along both grid legs totals seven.',
            },
          ],
        },
        {
          id: 'midpoint',
          title: 'Average coordinates to move halfway',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The midpoint of a segment lies halfway from one endpoint to the other. Start at the first coordinate and add half the displacement: x₁ + (x₂ − x₁)/2. Simplifying gives (x₁ + x₂)/2. The same reasoning applies independently to y. Averaging coordinates works because both components are moving through the same fraction of the segment.',
            },
            {
              kind: 'equation',
              label: 'Midpoint',
              lines: [
                {
                  left: 'M',
                  right: '((x₁ + x₂)/2, (y₁ + y₂)/2)',
                  reason:
                    'Average matching coordinates; do not average an x coordinate with a y coordinate.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'A midpoint across the origin',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'For endpoints (−4,3) and (2,7), the midpoint is (−1,5). From the first endpoint, the change is (+3,+2), exactly half the complete change (+6,+4). From the midpoint to the second endpoint, the change is the same again. This displacement check verifies the result without relying solely on memorizing the formula.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A midpoint is a point, not a distance or slope. Its coordinate signs can be negative. An endpoint can also be recovered when the other endpoint and midpoint are known: if their x coordinates satisfy m = (a + b)/2, then b = 2m − a, and likewise for y. Reflecting one endpoint through the midpoint is the geometric version of this algebra.',
            },
          ],
        },
        {
          id: 'slope',
          title: 'Compare changes rather than absolute positions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'For a nonvertical line, slope measures vertical change divided by horizontal change. All pairs of distinct points on the same straight line give the same ratio. A positive slope rises to the right; a negative slope falls to the right. A horizontal line has zero vertical change and therefore slope zero. A vertical line has zero horizontal change, so its slope is undefined rather than zero.',
            },
            {
              kind: 'equation',
              label: 'Slope',
              lines: [
                {
                  left: 'm',
                  right: '(y₂ − y₁) / (x₂ − x₁)',
                  reason: 'Use the same endpoint order in the numerator and denominator.',
                },
              ],
            },
            {
              kind: 'example',
              title: 'Interpret a slope with units',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose distance y in kilometers is plotted against time x in hours, and a line rises 12 kilometers over 3 hours. Its slope is 4 kilometers per hour. On a purely geometric drawing with both axes in meters, the slope is instead a length ratio. Labels and units determine what the numerical ratio means.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'In y = mx + b, b is the y coordinate where x = 0, so it is the y-intercept. To find an x-intercept, set y = 0 and solve for x. A point belongs to a graph when its coordinates satisfy the equation; visual closeness on a small drawing is only an approximation. When plotting a line, two distinct points determine it, but a general curved graph cannot safely be inferred from just two points. A steeper-looking line on a chart can also result from stretching one display axis, so compare numerical slopes and axis scales before interpreting appearance.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b3-coord-1',
        prompt: 'From (−3,2), move 5 units right and 4 units down. Where do you arrive?',
        options: [
          {
            id: '0',
            text: '(−8,6)',
          },
          {
            id: '1',
            text: '(2,6)',
          },
          {
            id: '2',
            text: '(−8,−2)',
          },
          {
            id: '3',
            text: '(2,−2)',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'Right adds to x and down subtracts from y, giving (−3 + 5, 2 − 4).',
        sectionIds: ['positions'],
        evidence: 'Right adds to x and down subtracts from y, giving (−3 + 5, 2 − 4).',
      },
      {
        id: 'b3-coord-2',
        prompt:
          'What is the straight-line distance between (−1,2) and (5,10), with both axes in meters?',
        options: [
          {
            id: '0',
            text: '10 m',
          },
          {
            id: '1',
            text: '14 m',
          },
          {
            id: '2',
            text: '100 m',
          },
          {
            id: '3',
            text: '2 m',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'The legs have lengths 6 and 8, so the distance is √(36 + 64) = 10 m.',
        sectionIds: ['positions', 'distance'],
        evidence: 'The legs have lengths 6 and 8, so the distance is √(36 + 64) = 10 m.',
      },
      {
        id: 'b3-coord-3',
        prompt:
          'For the same endpoints (−1,2) and (5,10), what is the shortest distance if travel is restricted to horizontal and vertical grid streets?',
        options: [
          {
            id: '0',
            text: '10 units',
          },
          {
            id: '1',
            text: '100 units',
          },
          {
            id: '2',
            text: '14 units',
          },
          {
            id: '3',
            text: '48 units',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'The route must cover six horizontal and eight vertical units, totaling 14.',
        sectionIds: ['positions', 'distance'],
        evidence: 'The route must cover six horizontal and eight vertical units, totaling 14.',
      },
      {
        id: 'b3-coord-4',
        prompt: 'What is the midpoint of the segment joining (−6,1) and (4,7)?',
        options: [
          {
            id: '0',
            text: '(−2,8)',
          },
          {
            id: '1',
            text: '(−1,4)',
          },
          {
            id: '2',
            text: '(5,3)',
          },
          {
            id: '3',
            text: '(−10,−6)',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Average matching coordinates: ((−6 + 4)/2, (1 + 7)/2) = (−1,4).',
        sectionIds: ['positions', 'midpoint'],
        evidence: 'Average matching coordinates: ((−6 + 4)/2, (1 + 7)/2) = (−1,4).',
      },
      {
        id: 'b3-coord-5',
        prompt: 'A segment has endpoint (2,−1) and midpoint (5,3). What is its other endpoint?',
        options: [
          {
            id: '0',
            text: '(8,7)',
          },
          {
            id: '1',
            text: '(3,4)',
          },
          {
            id: '2',
            text: '(7,2)',
          },
          {
            id: '3',
            text: '(10,6)',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation: 'Reflect through the midpoint: (2 × 5 − 2, 2 × 3 − (−1)) = (8,7).',
        sectionIds: ['positions', 'midpoint'],
        evidence: 'Reflect through the midpoint: (2 × 5 − 2, 2 × 3 − (−1)) = (8,7).',
      },
      {
        id: 'b3-coord-6',
        prompt: 'What is the slope of the line through (1,5) and (4,−1)?',
        options: [
          {
            id: '0',
            text: '2',
          },
          {
            id: '1',
            text: '−1/2',
          },
          {
            id: '2',
            text: '6',
          },
          {
            id: '3',
            text: '−2',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation: 'The vertical change is −6 and horizontal change is 3, giving slope −2.',
        sectionIds: ['positions', 'slope'],
        evidence: 'The vertical change is −6 and horizontal change is 3, giving slope −2.',
      },
      {
        id: 'b3-coord-7',
        prompt: 'Which statement about the vertical line x = 3 is correct?',
        options: [
          {
            id: '0',
            text: 'Its slope is zero.',
          },
          {
            id: '1',
            text: 'Its slope is undefined because horizontal change is zero.',
          },
          {
            id: '2',
            text: 'Its y-intercept must be 3.',
          },
          {
            id: '3',
            text: 'Its slope is 3 at every point.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation: 'Slope divides by horizontal change; a vertical line has a zero denominator.',
        sectionIds: ['positions', 'slope'],
        evidence: 'Slope divides by horizontal change; a vertical line has a zero denominator.',
      },
      {
        id: 'b3-coord-8',
        prompt: 'The line is y = 2x − 6. What is its x-intercept?',
        options: [
          {
            id: '0',
            text: '(0,−6)',
          },
          {
            id: '1',
            text: '(−3,0)',
          },
          {
            id: '2',
            text: '(3,0)',
          },
          {
            id: '3',
            text: '(0,3)',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'At the x-axis y = 0, so 0 = 2x − 6 and x = 3.',
        sectionIds: ['positions', 'slope'],
        evidence: 'At the x-axis y = 0, so 0 = 2x − 6 and x = 3.',
      },
      {
        id: 'b3-coord-9',
        prompt:
          'Every point is translated 7 units right and 2 units down. What happens to the straight-line distance between any two points?',
        options: [
          {
            id: '0',
            text: 'It increases by 9 units.',
          },
          {
            id: '1',
            text: 'It doubles.',
          },
          {
            id: '2',
            text: 'It stays the same because coordinate differences are unchanged.',
          },
          {
            id: '3',
            text: 'It depends only on the original midpoint.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation: 'A common translation cancels when endpoint differences are calculated.',
        sectionIds: ['positions', 'distance'],
        evidence: 'A common translation cancels when endpoint differences are calculated.',
      },
      {
        id: 'b3-coord-10',
        prompt: 'Which point lies on the line y = −3x + 4?',
        options: [
          {
            id: '0',
            text: '(2,−2)',
          },
          {
            id: '1',
            text: '(2,2)',
          },
          {
            id: '2',
            text: '(−2,−2)',
          },
          {
            id: '3',
            text: '(0,−4)',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Substituting x = 2 gives y = −6 + 4 = −2; the other points do not satisfy the equation.',
        sectionIds: ['positions', 'slope'],
        evidence:
          'Substituting x = 2 gives y = −6 + 4 = −2; the other points do not satisfy the equation.',
      },
    ],
  },
];
